import type { PublicStatement } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { errorMessage, get } from "../api";
import { StatementView } from "../components/statement-view";
import { Alert, Loading, formatCents, formatDate } from "../components/ui";
import { Pay } from "./PublicPay";

/** The customer's statement: several invoices on one page, paid all at once. No account; the link is the key. */
export function PublicStatementPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["public-statement", token], queryFn: () => get<PublicStatement>(`/api/public/statements/${token}`), retry: false, refetchInterval: (x) => (x.state.data?.pending.length ? 10_000 : false) });
  const set = (d: PublicStatement) => qc.setQueryData(["public-statement", token], d);
  useEffect(() => {
    if (q.data?.company.primaryColor) document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
  }, [q.data?.company.primaryColor]);

  if (q.isLoading)
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  if (q.error || !q.data)
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert>
      </div>
    );
  const s = q.data;
  return (
    <div className="min-h-dvh bg-paper">
      <StatementHeader s={s} />
      <main className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-6">
        <StatementTitle s={s} />
        <StatementView s={s} pdfHref={`/api/public/statements/${token}/pdf`} />
        {s.body.balanceCents === 0 ? (
          <Alert tone="ok">Paid in full. Thank you!</Alert>
        ) : s.pending.length ? (
          <Alert tone="info">Your payment of {formatCents(s.pending.reduce((n, p) => n + p.amountCents, 0))} is processing. Bank payments take a few business days; this page updates when it's done.</Alert>
        ) : !s.stripeKey || s.payableCents === 0 ? (
          <Alert tone="info">To pay, call us at {s.company.phone}.</Alert>
        ) : (
          <Pay<PublicStatement> base={`/api/public/statements/${token}`} resultKey="statement" what="Statement" amountCents={s.payableCents} customerName={s.customerName} cardAllowed={s.cardAllowed} cardLimitCents={s.cardLimitCents} surchargeBps={s.surchargeBps} stripeKey={s.stripeKey} onDone={set} />
        )}
        <p className="pb-6 text-center text-xs text-muted">
          {s.company.name}
          {s.company.email && ` · ${s.company.email}`} · Payments are processed securely by Stripe.
        </p>
      </main>
    </div>
  );
}

export function StatementHeader({ s }: { s: PublicStatement }) {
  return (
    <header className="bg-[var(--color-sidebar)] text-white">
      <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-4 py-4">
        <div className="flex items-center gap-3">
          {s.company.logoUrl && <img src={s.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
          <span className="text-lg font-semibold">{s.company.name}</span>
        </div>
        <span className="text-sm whitespace-nowrap text-white/80">{s.company.phone}</span>
      </div>
    </header>
  );
}

export function StatementTitle({ s }: { s: PublicStatement }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Statement{s.title && ` · ${s.title}`}</h1>
      <p className="text-ink-soft">
        For {s.customerName}
        {s.address && ` · ${s.address}`} · {formatDate(s.date)}
      </p>
    </div>
  );
}
